import { useMsal } from '@azure/msal-react';
import { Flex, Link, Text, useToast } from '@lego/klik-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader, PageLoadError } from '@/components/common';
import { CreateWorkspaceCard, CreateWorkspaceDialog, WorkspaceCard } from '@/components/workspace';
import { useDocumentTitle } from '@/hooks/common';
import { useWorkspaces } from '@/hooks/workspace/workspaces_resource';
import { useRegisterPageContext } from '@/providers/PageContextProvider';
import { ToastOptions } from '@/utility/ToastOptions';

export const WorkspacesPage = () => {
  useDocumentTitle('Workspaces');

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const toast = useToast();

  const { data, error, isLoading, mutate } = useWorkspaces();
  const { accounts, instance } = useMsal();

  useRegisterPageContext(data);

  const refreshWorkspaces = useCallback(async () => {
    if (!import.meta.env.VITE_ACCESS_TOKEN) {
      try {
        await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: [`api://${import.meta.env.VITE_CLIENT_ID}/user_impersonation`],
          forceRefresh: true,
        });
      } catch {
        // If silent refresh fails, the next API call handles it
        // via the existing InteractionRequiredAuthError logic in useCallApi
      }
    }
    await mutate();
  }, [accounts, instance, mutate]);

  const hasRefreshedOnMount = useRef(false);
  useEffect(() => {
    if (!hasRefreshedOnMount.current) {
      hasRefreshedOnMount.current = true;
      void refreshWorkspaces().catch(() => {
        // The data hook displays the API error toast.
      });
    }
  }, [refreshWorkspaces]);

  const handleHardRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refreshWorkspaces();
    } catch {
      // The data hook displays the API error toast.
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshWorkspaces]);

  if (isLoading) {
    return (
      <Flex flexDirection="row" flexWrap="wrap" gap={4} justifyContent="center">
        <Loader />
      </Flex>
    );
  }

  if (error || !data) {
    return <PageLoadError onRetry={refreshWorkspaces} />;
  }

  const workspaceList = data;

  return (
    <>
      <Flex flexDirection="row" flexWrap="wrap" gap={4} justifyContent="center">
        {workspaceList?.workspaces.map((workspace) => (
          <WorkspaceCard key={workspace.id} workspace={workspace} />
        ))}
        <CreateWorkspaceCard onClick={() => setIsCreateDialogOpen(true)} />
        <Flex basis="100%" justifyContent="center" mt={2}>
          <Text color="slate.500" fontSize="sm">
            Can&apos;t find your workspace?{' '}
            <Link isInline onClick={handleHardRefresh} size="sm">
              {isRefreshing ? 'Refreshing...' : 'Hard refresh'}
            </Link>
          </Text>
        </Flex>
      </Flex>
      <CreateWorkspaceDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCreated={() => {
          toast(
            ToastOptions.success(
              'Workspace created',
              'The workspace is being provisioned and will appear shortly.',
              8000
            )
          );
          void mutate();
        }}
      />
    </>
  );
};
